import { useMemo } from "react";
import { checkUrl, useInternalLinks } from "@/lib/internalLinks";

const CUSTOM = "__custom__";
const inputCls =
  "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] bg-white";

/**
 * Approved-link dropdown.
 * mode="internal": approved internal pages only (service Learn More / Estimate CTA).
 * mode="cta": approved pages, or a custom phone (tel:), email (mailto:) or outside link.
 */
export default function InternalLinkField({ label, value, onChange, mode = "internal", testid, allowEmpty = true, exclude = [], help }) {
  const targets = useInternalLinks();
  const v = value || "";
  const known = targets.some((t) => t.url === v);
  const isCustom = v !== "" && !known;
  const issue = useMemo(() => checkUrl(v, targets, mode), [v, targets, mode]);
  const options = targets.filter((t) => !exclude.includes(t.url) || t.url === v);

  return (
    <div className="block">
      {label && <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label}</span>}
      <select
        data-testid={testid}
        value={isCustom ? CUSTOM : v}
        onChange={(e) => onChange(e.target.value === CUSTOM ? (isCustom ? v : "tel:") : e.target.value)}
        className={inputCls}
      >
        {allowEmpty && <option value="">— No link —</option>}
        <optgroup label="Service pages">
          {options.filter((t) => t.kind === "service").map((t) => (
            <option key={t.url} value={t.url}>
              {t.label} ({t.url}){t.live ? "" : " (not published)"}
            </option>
          ))}
        </optgroup>
        <optgroup label="Site pages">
          {options.filter((t) => t.kind === "page").map((t) => (
            <option key={t.url} value={t.url}>
              {t.label} ({t.url})
            </option>
          ))}
        </optgroup>
        {(mode === "cta" || isCustom) && <option value={CUSTOM}>{mode === "cta" ? "Phone, email or outside link…" : "Current value (not approved)"}</option>}
      </select>
      {isCustom && (
        <input
          data-testid={testid ? `${testid}-custom` : undefined}
          value={v}
          onChange={(e) => onChange(e.target.value)}
          placeholder="tel:+19162247260, mailto:info@pacleans.com or https://…"
          className={`${inputCls} mt-2`}
        />
      )}
      {issue ? (
        <p
          data-testid={testid ? `${testid}-issue` : undefined}
          className={`mt-1.5 text-xs ${issue.level === "error" ? "text-rose-700" : "text-amber-700"}`}
        >
          {issue.level === "error" ? "Not allowed: " : "Heads up: "}
          {issue.message}
        </p>
      ) : help ? (
        <p className="mt-1.5 text-xs text-slate-500">{help}</p>
      ) : null}
    </div>
  );
}
